import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { auth, miConjunto, miNegocio } from "../api/endpoints";
import {
  errorMessage,
  setUnauthorizedHandler,
  tokenStore,
  userStore,
} from "../lib/http";
import { negocioActivo } from "../lib/negocioActivo";
import {
  AMBITO_CONJUNTO,
  AMBITO_NEGOCIO,
  ROLES_DE_ALIADO,
  ambitoDeRol,
} from "../lib/constants";
import { anotarSesion } from "../lib/monitoreo";

/* =========================================================================
   SESIÓN Y PERMISOS

   El panel hospeda dos oficios: el conjunto (roles 5 y 6) y el negocio
   (rol 2). Los dos son gente que trabaja CON la plataforma y no EN ella; el
   rol 4 tiene su propio panel y acá no entra, porque compartir la puerta
   ampliaría en silencio quién llega a la administración.

   LO QUE CAMBIA ENTRE LOS DOS ES UNA LLAMADA. `/conjunto/me` y `/negocio/me`
   devuelven la MISMA forma —permisos por clave con `view` y `manage`—, así que
   el menú, el guardia de rutas y el armazón no saben cuál de los dos está
   activo ni les hace falta. El ámbito solo decide qué endpoint se pregunta y
   qué secciones existen.

   Ocultar una sección es comodidad de la interfaz: el servidor la rechaza
   igual si alguien escribe la URL a mano.
   ======================================================================== */

const AuthCtx = createContext(null);

/* El panel de aliados no publica reglas de operación: no gestiona tarifas ni
   comisiones. La clave se conserva para no partir la forma del contexto. */
const REGLAS_POR_DEFECTO = null;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => userStore.get());
  const [permisos, setPermisos] = useState(null);
  const [area, setArea] = useState(null);
  const [nivel, setNivel] = useState(null);
  const [reglas] = useState(null);
  /** Los locales que administra, cuando el ámbito es negocio. */
  const [negocios, setNegocios] = useState([]);
  /** Las cifras del encabezado, tal como las calcula el servidor. */
  const [stats, setStats] = useState(null);
  /**
   * Se refleja en estado —además de vivir en el módulo que lee axios— para que
   * las pantallas puedan meterlo en su clave de consulta. Sin eso, cambiar de
   * local dejaría en pantalla los datos del anterior hasta que algo los
   * invalidara, que es exactamente el error que no se puede cometer acá.
   */
  const [negocioId, setNegocioId] = useState(() => negocioActivo.get());
  const [booting, setBooting] = useState(() => Boolean(tokenStore.get()));

  const ambito = ambitoDeRol(user?.rol);

  const clear = useCallback(() => {
    tokenStore.set(null);
    userStore.set(null);
    negocioActivo.set(null);
    setUser(null);
    setPermisos(null);
    setArea(null);
    setNivel(null);
    setNegocios([]);
    setStats(null);
    setNegocioId(null);
    anotarSesion(null);
  }, []);

  /**
   * Trae el ámbito de quien entró: su conjunto o su negocio, y sus permisos.
   *
   * Si falla se deja el mapa VACÍO en vez de suponer acceso total: ante la duda
   * el panel muestra de menos, nunca de más. Un menú incompleto se resuelve
   * recargando; uno que ofrece secciones prohibidas llena la pantalla de 403.
   */
  const cargarPermisos = useCallback(async (rol) => {
    const cual = ambitoDeRol(rol);

    try {
      if (cual === AMBITO_NEGOCIO) {
        const datos = await miNegocio.cargar();

        setPermisos(datos?.permissions ?? {});
        /* El hueco de "área" lo ocupa el NEGOCIO y el de "nivel", el oficio.
           Se conserva la forma para reutilizar el armazón sin tocarlo. */
        setArea(
          datos?.business
            ? { ...datos.business, name: datos.business.name }
            : null,
        );
        setNivel(datos?.role ?? null);
        setNegocios(datos?.businesses ?? []);
        setStats(datos?.stats ?? null);

        /*
         * El servidor decide cuál quedó activo, no el cliente. Si la cabecera
         * iba vacía respondió con el primero, y guardarlo acá evita que la
         * próxima petición vuelva a ir sin decidir.
         */
        const activo = datos?.business?.busines_id ?? null;
        negocioActivo.set(activo);
        setNegocioId(activo);

        anotarSesion({
          id: userStore.get()?.user_id,
          negocio: activo,
          rol: datos?.role,
        });

        return datos;
      }

      const datos = await miConjunto.cargar();

      setPermisos(datos?.permissions ?? {});
      setArea(datos?.complex ?? null);
      setNivel(datos?.role ?? null);
      setNegocios([]);
      setStats(datos?.stats ?? null);

      /*
       * Al monitoreo va el identificador y el ÁMBITO, que es lo que explica por
       * qué esa persona veía esa pantalla. Ni correo ni nombre: para arreglar
       * un error hace falta el contexto, no la identidad.
       */
      anotarSesion({
        id: userStore.get()?.user_id,
        conjunto: datos?.complex?.complex_id,
        rol: datos?.role,
      });

      return datos;
    } catch {
      setPermisos({});
      setArea(null);
      setNivel(null);
      setStats(null);
      return null;
    }
  }, []);

  /**
   * Cambiar de local.
   *
   * Se escribe PRIMERO el negocio activo y solo después se recarga: al revés,
   * la petición de recarga saldría todavía con la cabecera anterior y volvería
   * con los datos del local que se acaba de abandonar.
   *
   * Si el servidor lo rechaza —403, porque ese local no es suyo— se vuelve al
   * anterior en vez de dejar el panel apuntando a ninguna parte.
   */
  const cambiarNegocio = useCallback(
    async (id) => {
      const anterior = negocioActivo.get();
      if (Number(id) === Number(anterior)) return { ok: true };

      negocioActivo.set(id);
      setNegocioId(Number(id));

      const datos = await cargarPermisos(user?.rol);

      if (!datos) {
        negocioActivo.set(anterior);
        setNegocioId(anterior);
        await cargarPermisos(user?.rol);
        return { ok: false, message: "No se pudo cambiar de negocio." };
      }

      return { ok: true };
    },
    [cargarPermisos, user?.rol],
  );

  /** Vuelve a pedir las cifras del encabezado sin recargar la página. */
  const refrescarResumen = useCallback(
    () => cargarPermisos(user?.rol),
    [cargarPermisos, user?.rol],
  );

  // Un 401 en cualquier petición cierra la sesión en toda la app.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clear();
    });
  }, [clear]);

  // Al recargar con token guardado se revalida contra /profile: el token
  // pudo revocarse desde otro dispositivo y no tiene sentido pintar el panel
  // entero para que después falle cada consulta.
  useEffect(() => {
    let vivo = true;

    // Sin token no hay nada que revalidar, y `booting` ya arranca apagado.
    if (!tokenStore.get()) return;

    auth
      .profile()
      .then(async (perfil) => {
        if (!vivo) return;
        const u = perfil?.user ?? perfil;

        if (!ROLES_DE_ALIADO.includes(Number(u?.rol))) {
          clear();
          return;
        }

        userStore.set(u);
        setUser(u);
        // Los permisos no se guardan en localStorage: si a alguien le cambian
        // el acceso, una copia local haría que siguiera viendo el menú
        // anterior hasta cerrar sesión.
        await cargarPermisos(u?.rol);
      })
      .catch(() => {
        if (vivo) clear();
      })
      .finally(() => {
        if (vivo) setBooting(false);
      });

    return () => {
      vivo = false;
    };
  }, [clear, cargarPermisos]);

  const login = useCallback(
    async (email, password, codigo) => {
      try {
        const data = await auth.login(email, password, codigo);
        const u = data?.user;
        const cual = ambitoDeRol(u?.rol);

        if (!cual) {
          return {
            ok: false,
            message:
              "Esta cuenta no es de un aliado. Si administras un negocio o un conjunto, pídele al equipo que te dé de alta.",
          };
        }

        tokenStore.set(data.token);
        userStore.set(u);
        setUser(u);

        /*
         * Se limpia el local recordado al entrar: puede ser el de la sesión
         * anterior, de otra persona en el mismo navegador. Con él puesto, la
         * primera petición saldría pidiendo una tienda ajena y el panel
         * abriría en un 403 en vez de en su propia tienda.
         */
        negocioActivo.set(null);
        setNegocioId(null);

        const datos = await cargarPermisos(u?.rol);

        // Entrar sin ámbito es entrar a un panel vacío. Se dice acá, en el
        // login, en vez de dejar que la persona descubra un menú en blanco.
        const sinAmbito =
          !datos ||
          (cual === AMBITO_CONJUNTO && !datos.complex) ||
          (cual === AMBITO_NEGOCIO && !datos.business);

        if (sinAmbito) {
          // Se deshace la sesión: dejar el token guardado con el login
          // rechazado haría que la siguiente recarga entrara a un panel sin
          // menú.
          clear();

          return {
            ok: false,
            message:
              cual === AMBITO_NEGOCIO
                ? "Tu cuenta no administra ningún negocio. Pídele al equipo que te asigne uno."
                : "Tu cuenta no está vinculada a ningún conjunto. Pídele al equipo que te asigne uno.",
          };
        }

        return { ok: true };
      } catch (e) {
        /*
         * El servidor pide el segundo factor con `reason`, y la pantalla lo
         * necesita para saber si tiene que enseñar el campo del código o si es
         * un error de credenciales corriente. Sin distinguirlo, la persona
         * vería "no se pudo iniciar sesión" con la clave bien puesta.
         */
        const motivo = e?.response?.data?.reason ?? null;

        return {
          ok: false,
          motivo,
          message: errorMessage(e, "No se pudo iniciar sesión."),
        };
      }
    },
    [cargarPermisos, clear],
  );

  const logout = useCallback(async () => {
    // Se invalida el token en el servidor, pero la sesión local se limpia
    // pase lo que pase: si la red falla, el usuario igual queda fuera.
    try {
      await auth.logout();
    } catch {
      /* sin red o token ya vencido */
    } finally {
      clear();
    }
  }, [clear]);

  const value = useMemo(() => {
    /*
     * Mientras los permisos no han llegado (`null`) se responde que NO a todo.
     * Responder que sí "por si acaso" haría parpadear el menú completo antes de
     * recortarse, que es peor: se alcanza a ver lo que no corresponde.
     */
    const puedeVer = (modulo) => Boolean(permisos?.[modulo]?.view);
    const puedeGestionar = (modulo) => Boolean(permisos?.[modulo]?.manage);

    return {
      user,
      /** 'conjunto' o 'negocio'. Decide qué menú y qué rutas existen. */
      ambito,
      area,
      nivel,
      permisos,
      /** Cifras del encabezado, calculadas por el servidor. */
      stats,
      /** Locales que administra. Vacío salvo en el ámbito negocio. */
      negocios,
      /** Cuál está activo. Va en la clave de consulta de cada pantalla. */
      negocioId,
      cambiarNegocio,
      refrescarResumen,
      reglas: reglas ?? REGLAS_POR_DEFECTO,
      puedeVer,
      puedeGestionar,
      recargarPermisos: refrescarResumen,
      booting,
      login,
      logout,
      isAuthenticated: Boolean(user),
    };
  }, [
    user,
    ambito,
    area,
    nivel,
    reglas,
    permisos,
    stats,
    negocios,
    negocioId,
    cambiarNegocio,
    refrescarResumen,
    booting,
    login,
    logout,
  ]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth necesita estar dentro de <AuthProvider>");
  return ctx;
};
